import React, { useState, useEffect } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { testWebhook, fetchWebhookEvents } from '~/redux/actions/webhooks-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Components
import OlcDropdown from '~/components/General/OlcDropdown';
import OlcFormModal from '~/components/General/OlcFormModal';

// MUI Components
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, InputLabel } from '@mui/material';

// Styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';

const TestWebhookModal = ({ openModal, handleCloseModal, webhookId, onTestComplete }) => {
  const formId = 'test-webhook-form';
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventError, setEventError] = useState('');
  const [testing, setTesting] = useState(false);

  const dispatch = useDispatch();

  const eventOptions = useSelector((state) =>
    state.webhooksReducers.events.map((event, index) => ({
      ...event,
      id: index + 1,
      label: event.title,
    }))
  );

  const onClose = () => {
    handleCloseModal();
    setSelectedEvent(null);
    setEventError('');
  };

  const testWebhookHandler = async (event) => {
    try {
      event.preventDefault();

      if (!selectedEvent) {
        setEventError('Please select an event to test');
        setTesting(false);
        return;
      }

      setTesting(true);

      const eventPayload = {
        event: selectedEvent?.event,
      };

      const response = await dispatch(testWebhook(webhookId, eventPayload));

      if (response.status === 200) {
        dispatch(
          success(
            'Your webhook is being processed - check the logs tab to see the response details.'
          )
        );
        onClose();
        // Trigger parent component to refresh webhook logs
        if (onTestComplete) {
          onTestComplete();
        }
      } else {
        dispatch(failure('Failed to test webhook'));
      }
    } catch (err) {
      dispatch(failure('An error occurred while testing the webhook'));
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    if (openModal) {
      setSelectedEvent(null);
      setEventError('');
      dispatch(fetchWebhookEvents());
    }
  }, [openModal, dispatch]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title="Test Webhook"
      className="testWebhookFormModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={testing}>
            Cancel
          </OlcButton>
          <OlcButton variant="primary" type="submit" form={formId} disabled={testing}>
            {testing ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              'Test'
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="modalFormWrapper">
        <form id={formId} onSubmit={testWebhookHandler} noValidate>
          <Box className="modalFlexArea">
            <Box>
              <InputLabel htmlFor="event-select" className="inputLabel formLabel">
                Select Event
                <span className="olcRequired">*</span>
              </InputLabel>
              <OlcDropdown
                placeholder="Choose an event to test"
                options={eventOptions}
                selectedValue={selectedEvent}
                setSelectedValue={(val) => {
                  setSelectedEvent(val);
                  if (eventError) setEventError('');
                }}
                search={() => {}}
                updateErrors={() => {}}
                filterAutoComplete
                className="newAutocompleteDesign olcNewDropdown"
                newDesign={true}
                error={!!eventError}
                readOnlyInput={true}
                disableClearable={true}
              />
              {eventError && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {eventError}
                </Typography>
              )}

              {/* Test Event Note */}
              <Typography
                className="testEventNote"
                sx={{
                  color: '#666',
                  fontFamily: 'Inter',
                  fontSize: '13px',
                  fontWeight: '400',
                  marginTop: '8px',
                  lineHeight: '18px',
                  fontStyle: 'italic',
                }}
              >
                <strong>Note:</strong> Test webhooks will include an extra <code>isTest: true</code>{' '}
                field in the payload, which you can use to identify test events versus actual events
                at your endpoint.
              </Typography>
            </Box>
          </Box>
        </form>
      </Box>
    </OlcFormModal>
  );
};

export default TestWebhookModal;
